export {bahaiYearDefinitions, bahaiYearDefinition, bahaiYearLabel} from '@liquid-spirit/bahai-calendar/years';
import {readBahaiYear} from '@liquid-spirit/bahai-calendar/years';
export class CommunityYearContractError extends Error {
  constructor(detail, request = false) {super(`Invalid community year ${detail}`); this.name = 'CommunityYearContractError'; this.code = request ? 'INVALID_API_REQUEST' : 'INVALID_API_RESPONSE';}
}
const responseBody = payload => payload?.success === undefined && payload?.data?.success !== undefined ? payload.data : payload;
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const identifier = value => typeof value === 'string' && /^[a-f0-9]{24}$/.test(value);
const instant = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T.*Z$/.test(value) && Number.isFinite(Date.parse(value));
export const yearSelector = value => {
  if (value === 'current' || value === undefined) return 'current';
  try {return readBahaiYear(value);} catch {throw new CommunityYearContractError('selector', true);}
};
export const isBahaiYearDefinition = value => {
  if (!record(value)) return false;
  try {
    const number = readBahaiYear(value.number);
    return value.id === `badi:${number}` && value.label === `${number} B.E.` && value.gregorianStartYear === number + 1843
      && /^\d{4}-\d{2}-\d{2}$/.test(value.startDate) && /^\d{4}-\d{2}-\d{2}$/.test(value.endDateExclusive)
      && Number.isFinite(Date.parse(value.startDate)) && Number.isFinite(Date.parse(value.endDateExclusive)) && value.endDateExclusive > value.startDate
      && typeof value.ruleVersion === 'string' && Boolean(value.ruleVersion);
  } catch {return false;}
};
export function isCommunityYearContext(value) {
  const p = value?.reporting;
  try {readBahaiYear(value?.currentYearId);} catch {return false;}
  return record(value) && identifier(value.communityId) && isBahaiYearDefinition(value.year)
    && value.id === `${value.communityId}:${value.year.id}` && /^badi:[1-9][0-9]*$/.test(value.currentYearId)
    && typeof value.recorded === 'boolean' && record(p) && p.basis === 'community-civil-date-v1'
    && p.ruleVersion === value.year.ruleVersion && typeof p.timeZone === 'string' && Boolean(p.timeZone)
    && ['community', 'legacyDefault'].includes(p.timeZoneSource) && p.startDate === value.year.startDate && p.endDateExclusive === value.year.endDateExclusive
    && instant(p.startsAt) && instant(p.endsBefore) && Date.parse(p.endsBefore) > Date.parse(p.startsAt);
}
export function readBahaiYearsResponse(payload) {
  const body = responseBody(payload);
  if (body?.success !== true || !Array.isArray(body.data) || !body.data.every(isBahaiYearDefinition)) throw new CommunityYearContractError('definitions response');
  return payload;
}
export function readCommunityYearResponse(payload) {
  const body = responseBody(payload);
  if (body?.success !== true || !isCommunityYearContext(body.data)) throw new CommunityYearContractError('context response');
  return payload;
}
export function readCommunityYearStatisticsResponse(payload) {
  const body = responseBody(payload), value = body?.data;
  const count = value => Number.isSafeInteger(value) && value >= 0;
  if (body?.success !== true || !record(value) || !isCommunityYearContext(value.context) || !instant(value.asOf) || value.measurementVersion !== 'community-year-statistics-v1'
    || !record(value.counts) || !['completedSessions', 'recordedSessions', 'scheduledEvents'].every(key => count(value.counts[key]))
    || !record(value.quality) || !['legacyDateOnlySessions', 'legacyDateOnlyEvents', 'unscopedSessions', 'undatedSessions', 'undatedEvents'].every(key => count(value.quality[key]))
    || value.financial !== null && (!record(value.financial) || value.financial.basis !== 'accountingDate' || !count(value.financial.unassignedEntries) || !Array.isArray(value.financial.byCurrency)
      || !value.financial.byCurrency.every(row => /^[A-Z]{3}$/.test(row.currency) && Number.isFinite(row.moneyIn) && row.moneyIn >= 0 && Number.isFinite(row.moneyOut) && row.moneyOut >= 0))) throw new CommunityYearContractError('statistics response');
  return payload;
}
export function parseBahaiYearQuery(value) {
  if (value == null || value === '' || value === 'current') return 'current';
  if (typeof value !== 'string' || !/^[1-9][0-9]*$/.test(value)) throw new CommunityYearContractError('URL selector', true);
  return yearSelector(Number(value));
}
export function withBahaiYearQuery(search, value) {
  const selected = yearSelector(value), params = new URLSearchParams(search);
  if (selected === 'current') params.delete('bahaiYear'); else params.set('bahaiYear', String(selected));
  return params.toString();
}

export function createCommunityYearsClient(adapter) {
  return {
    async statistics(year = 'current', options = {}) {
      const selected = yearSelector(year);
      const result = responseBody(readCommunityYearStatisticsResponse(await adapter.get(`/api/community-years/${selected}/statistics`, options)));
      if (selected !== 'current' && result.data.context.year.number !== selected) throw new CommunityYearContractError('statistics year mismatch');
      return result;
    },
    definitions: async (options = {}) => responseBody(readBahaiYearsResponse(await adapter.get('/api/bahai-years', options))),
    async context(year = 'current', options = {}) {
      const selected = yearSelector(year);
      const result = responseBody(readCommunityYearResponse(await adapter.get(`/api/community-years/${selected}`, options)));
      if (selected !== 'current' && result.data.year.number !== selected) throw new CommunityYearContractError('context year mismatch');
      return result;
    },
  };
}
